import React, { useState, useEffect } from "react";
import {
  Grid2,
  TextField,
  Button,
  Typography,
  Box,
  InputLabel,
  FormControl,
  Input,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment
} from "@mui/material";
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import Paper from "@mui/material/Paper";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePermission } from "../authentication/permission-providor/UserPermission";
import AddButton from "../../themes/overrides/AddButton";
import LoadingOverlay from "../../components/LoadingOverlay";
import LoadingSpinner from "../../components/icons/LoadingSpinner";

import SearchField from "../../themes/overrides/SearchField";
import EditIcon from "../../components/icons/EditIcon";
import RemoveIcon from "../../components/icons/RemoveIcon";
import NumberIcon from "../../components/icons/NumberIcon";
import ObjectIcon from "../../components/icons/ObjectIcon";
import RefArrayIcon from "../../components/icons/RefArrayIcon";
import RefIcon from "../../components/icons/RefIcon";
import TextIcon from "../../components/icons/TextIcon";
import RichTextIcon from "../../components/icons/RichTextIcon";
import CorrectIcon from "../../components/icons/CorrectIcon";
import CustomizedBreadcrumbs from "../../components/CustomizedBreadcrumbs";
import FieldIcon from "../../assets/images/content_circle.svg"

import {
  updateContentType,
  addField,
  submitFormData,
  resetForm,
  updateField,
  getContentTypeById,
  removeField,
} from "../../actions/contentActions";
import FieldModal from "./FieldModal";
import MediaIcon from "../../components/icons/MediaIcon";
import BooleanIcon from "../../components/icons/BooleanIcon";
import DateIcon from "../../components/icons/DateIcon";
import CodeIcon from "../../components/icons/CodeIcon";
import PreviewUrlModal from "./PreviewUrlModal";

// Field types that carry version control when added via the field editor —
// kept in sync with FieldModal.js so AI-seeded fields match a manual add.
const VERSION_CONTROL_TYPES = ['string', 'number', 'richText', 'media', 'dateTime', 'array', 'code'];

// Derive a camelCase, alphanumeric field id from a name (mirrors FieldModal's id rule).
const slugifyFieldId = (name) =>
  (name || '')
    .trim()
    .split(/[\s\-_]+/)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('')
    .replace(/[^a-zA-Z0-9]/g, '') || 'field';

const Fields = () => {
  const { id, formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams();
  const fields = useSelector((state) => state.form.fields) || [];
  const contentData = useSelector((state) => state.form);

  const [editFieldData, setEditFieldData] = useState(null);
  const [editFieldIndex, setEditFieldIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [reorderedFields, setReorderedFields] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true); // Initial state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasPermission } = usePermission();

  const [successMessage, setSuccessMessage] = useState(""); // State for success message
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [previewURLModalOpen, setPreviewURLModalOpen] = useState(false);
  const [previewSubmitLoading, setPreviewSubmitLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (formId) {
        setIsLoading(true);
        setEditMode(true);
        await dispatch(getContentTypeById(workspaceId, formId));
        // reset();

        // Seed AI-proposed fields (from the finder flow) as an unsaved draft.
        const prefillFields = location.state?.prefillFields;
        if (Array.isArray(prefillFields) && prefillFields.length > 0) {
          const usedIds = new Set();
          prefillFields.forEach((f) => {
            let fieldId = slugifyFieldId(f.name);
            while (usedIds.has(fieldId)) fieldId = `${fieldId}X`;
            usedIds.add(fieldId);
            dispatch(
              addField({
                id: fieldId,
                name: f.name,
                type: f.type,
                required: !!f.required,
                localizations: false,
                version_control: VERSION_CONTROL_TYPES.includes(f.type),
                ...(f.type === 'array' ? { array_type: f.array_type || 'string' } : {}),
              })
            );
          });
          setIsDisabled(false); // enable Save so the user can persist the draft
          // Clear nav state so a refresh/re-mount doesn't re-add the fields.
          navigate(location.pathname, { replace: true, state: {} });
        }
      } else {
        setEditMode(false);
        // dispatch(resetForm());
        // reset();
      }
      setIsLoading(false);
    };
    loadData();
  }, [formId, workspaceId, dispatch, reset]);

  useEffect(() => {
    if (editMode && contentData) {
      setValue("name", contentData?.name || "");
      setValue("slug", contentData?.slug || "");
      setValue("description", contentData?.description || "");
    }
  }, [editMode, contentData, setValue]);

  const onSubmit = async () => {
    try {
      setIsSaveLoading(true);

      // Find the current findable field id
      const activeFields = reorderedFields || fields;
      const findableFields = activeFields.filter(f => f.findable).map(f => f.id);
      const findableFieldId = findableFields.length > 0 ? findableFields[0] : null;

      if (editMode) {
        const formData = {
          name: contentData.name,
          slug: contentData.slug,
          description: contentData.description,
          fields: reorderedFields || fields,
          workspaceId: contentData.workspaceId,
          _id: contentData._id,
          findableField: findableFieldId,
          findableFields: findableFields
        };
        await dispatch(updateContentType(formId, formData));

        await new Promise(resolve => setTimeout(resolve, 100));

        await dispatch(getContentTypeById(workspaceId, formId));

        setReorderedFields(null);
        setSuccessMessage("Content type successfully updated."); // Set success message
        setOpenSnackbar(true);
        setSeverity("success");
        setIsDisabled(true);

        // dispatch(resetForm());
        // reset();
      } else {
        const cleanContentData = {
          name: contentData.name,
          slug: contentData.slug,
          description: contentData.description,
          fields: contentData.fields,
          workspaceId: contentData.workspaceId,
          findableField: findableFieldId,
          findableFields: findableFields
        };

        dispatch(submitFormData(cleanContentData));
        dispatch(getContentTypeById(workspaceId, formId));

        setSuccessMessage("Content type successfully updated."); // Set success message
        setOpenSnackbar(true);
        setSeverity("success");
        setIsDisabled(true);

        // dispatch(resetForm());
        // reset();
      }
    } catch (error) {
      setSeverity("error");
      setSuccessMessage("Error updating content type");
      setOpenSnackbar(true);
      // console.error("Error saving content type:", error);
    } finally {
      setIsSaveLoading(false)
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleAddField = (fieldData) => {
    setIsDisabled(false);
    // dispatch(addField(fieldData));
    if (editFieldIndex !== null) {
      if (reorderedFields) {
        const updatedFields = reorderedFields.map(field =>
          field.id === editFieldIndex ? { ...field, ...fieldData } : field
        );
        setReorderedFields(updatedFields);
      }
      dispatch(updateField(editFieldIndex, fieldData));
    } else {
      dispatch(addField(fieldData));
    }
    setModalOpen(false);
    setEditFieldIndex(null);
    setEditFieldData(null);
  };


  const handleFieldChange = (index, key, value) => {
    dispatch(updateField(index, { [key]: value }));
  };
  const handleRemoveField = (index) => {
    setIsDisabled(false);

    if (reorderedFields) {
      const updatedReorderedFields = reorderedFields.filter(field => field.id !== index);
      setReorderedFields(updatedReorderedFields);
    }

    dispatch(removeField(index));
  };

  const handlePreviewSubmit = async (url) => {
    try {
      setPreviewSubmitLoading(true)

      const data = {
        "previewUrl": url
      }

      const response = await dispatch(updateContentType(formId, data));
      console.log(response);

      setSuccessMessage("Preview URL added successfully!"); // Set success message
      setOpenSnackbar(true);
      setSeverity("success");
    } catch (error) {
      setSeverity("error");
      setSuccessMessage("failed to add preview URL");
      setOpenSnackbar(true);
    } finally {
      setPreviewSubmitLoading(false)
    }
  };

  const openEditModal = (index) => {
    const fieldsArray = reorderedFields || fields;
    const fieldToEdit = fieldsArray.find((field) => field.id === index);

    if (fieldToEdit) {
      setEditMode(true);
      setEditFieldIndex(index);
      setEditFieldData(fieldToEdit);
      setModalOpen(true);
    }
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const openPreviewUrlModal = () => {
    setPreviewURLModalOpen(true);
  }

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(reorderedFields || fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderedWithIds = items.map(item => ({
      ...item,
      id: item.id
    }));

    setReorderedFields(reorderedWithIds);
    setIsDisabled(false);
  };

  const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
      const animation = requestAnimationFrame(() => setEnabled(true));
      return () => {
        cancelAnimationFrame(animation);
        setEnabled(false);
      };
    }, []);
    if (!enabled) {
      return null;
    }
    return <Droppable {...props}>{children}</Droppable>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "string":
        return <TextIcon />;
      case "number":
        return <NumberIcon />;
      case "richText":
        return <RichTextIcon />;
      case "array":
        return <RefArrayIcon />;
      case "reference":
        return <RefIcon />;
      case "object":
        return <ObjectIcon />;
      case "dateTime":
        return <DateIcon />;
      case "media":
        return <MediaIcon />;
      case "boolean":
        return <BooleanIcon />;
      case "code":
        return <CodeIcon />;
      default:
        return <TextIcon />;
    }
  };

  const getTickOptions = (locTickOptions) => {

    switch (locTickOptions) {
      case true:
        return <CorrectIcon />
      case false:
        return <span className="ml-2">-</span>
      default:
        return <span className="ml-2">-</span>

    }
  };

  const filteredRows = Array.isArray(reorderedFields || fields)
    ? (reorderedFields || fields).filter((field) => {
      const searchLower = searchTerm.toLowerCase().trim();


      const nameMatch = field.name ? field.name.toLowerCase().includes(searchLower) : false;
      const typeMatch = field.type ? field.type.toLowerCase().includes(searchLower) : false;
      const idMatch = field.id ? field.id.toString().toLowerCase().includes(searchLower) : false;

      return nameMatch || typeMatch || idMatch;
    })
      .map((field) => ({
        ...field,
        id: field.id
      }))
    : [];


  const DraggableFieldsTable = () => (
    <DragDropContext onDragEnd={handleDragEnd}>
      <StrictModeDroppable droppableId="fields">
        {(provided) => (
          <TableContainer
            component={Paper}
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{
              overflowX: 'auto',
              '& .MuiTableCell-root': {
                padding: '8px 16px',
                height: '48px',
                whiteSpace: 'nowrap'
              }
            }}
          >
            <Table sx={{ tableLayout: 'fixed', minWidth: isMobile ? '800px' : 'auto' }}>
              <colgroup>
                <col style={{ width: '50px' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell width="50px" sx={{ padding: '8px' }}></TableCell>
                  <TableCell width="25%">Name</TableCell>
                  <TableCell width="20%">Type</TableCell>
                  <TableCell width="15%">Localization</TableCell>
                  <TableCell width="15%">Required</TableCell>
                  <TableCell width="15%">Version Control</TableCell>
                  <TableCell width="10%">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(filteredRows).map((row, index) => (
                  <Draggable
                    key={String(row.id)}
                    draggableId={String(row.id)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          background: snapshot.isDragging ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
                          height: '48px',
                          tableLayout: 'fixed',
                          '& > td': {
                            padding: '8px 16px',
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          },
                          ...provided.draggableProps.style
                        }}
                      >
                        <TableCell
                          {...provided.dragHandleProps}
                          sx={{
                            cursor: 'grab',
                            width: '65px !important',
                            padding: '8px !important',
                            '&:active': {
                              cursor: 'grabbing'
                            }
                          }}
                        >
                          <DragIndicatorIcon color="action" fontSize="small" />
                        </TableCell>
                        <TableCell sx={{ width: '30%' }}>{row.name}</TableCell>
                        <TableCell sx={{ width: '20%' }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            {getTypeIcon(row.type)}
                            <Typography variant="body2">{row.type}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: '15%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', }}>
                            {getTickOptions(row.localizations)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: '15%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', }}>
                            {getTickOptions(row.required)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: '10%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', }}>
                            {getTickOptions(row.version_control)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: '10%' }}>
                          <Box sx={{
                            display: 'flex',
                            gap: 1
                          }}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => openEditModal(row.id)}
                              disabled={!hasPermission('editor')}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleRemoveField(row.id)}
                              disabled={!hasPermission('editor')}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );

  return (
    <>
      <Grid2 container spacing={2} alignItems="center" justifyContent="center">
        <Grid2 size={{ xs: 12, md: 6.2 }} display="flex" alignItems="center" sx={{ marginLeft: { xs: 4, md: 0 } }}>
          <Box component="img" sx={{ width: { xs: 32, md: 40 }, marginRight: { xs: 2, md: 4 } }} src={FieldIcon} />
          <Typography variant="h1">List of Fields of "{contentData.name}"</Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 5.8 }} sx={{ display: 'flex', gap: 2 }}>
          <Box alignItems="flex-end" justifyContent="flex-end">
            <AddButton
              // to={`/${workspaceId}/content-type`}
              onClick={openModal}
              label="Add Field"
              color="secondary"
              disabled={!hasPermission('editor')}
            />
          </Box>
          <Box>
            <AddButton
              startIcon={<InsertLinkIcon />}
              // to={`/${workspaceId}/content-type`}
              onClick={openPreviewUrlModal}
              label="Set preview URL"
              color="secondary"
              disabled={!hasPermission('editor')}
            />
          </Box>
          <Box alignItems="flex-end" justifyContent="flex-end">
            <AddButton
              startIcon={
                isSaveLoading && <LoadingSpinner color="white" sx={{ marginRight: 1 }} />
              }
              type="submit"
              label={isSaveLoading ? "Saving..." : "Save"}
              onClick={onSubmit}
              disabled={isDisabled || !hasPermission('editor')}
              style={{
                cursor: isDisabled || !hasPermission('editor') ? "not-allowed" : "pointer",
                opacity: isDisabled || !hasPermission('editor') ? 0.8 : 1,
              }}
              sx={{
                '&.Mui-disabled': {
                  color: 'white',
                }
              }}
            />

          </Box>
        </Grid2>
        <Grid2 size={12}>
          <CustomizedBreadcrumbs listOne={contentData.name} />
        </Grid2>
        <Grid2 size={12} container spacing={2} alignItems="center">
          {/* <Grid2 size={2}>
            <Typography variant="p">Recent Fields</Typography>
          </Grid2> */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <SearchField
              placeholder="Search Field"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={handleSearch}
              aria-label="Search fields"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid2>
          <Grid2 size={4}></Grid2>
          <Grid2 size={12}>
            <Paper>
              <Box sx={{ width: "100%", minHeight: 100, alignContent: "center" }}>
                {isLoading ? (
                  <LoadingOverlay />
                ) : fields.length > 0 ? (
                  <DraggableFieldsTable />
                ) : (
                  <Typography variant="body1" gutterBottom align="center">
                    No fields
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid2>
        </Grid2>

        <FieldModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditFieldIndex(null);
            setEditFieldData(null);
          }}
          onAddField={handleAddField}
          handleFieldChange={handleFieldChange}
          fieldData={editFieldData}
          editMode={editMode}
        />
        <PreviewUrlModal
          isOpen={previewURLModalOpen}
          onClose={() => {
            setPreviewURLModalOpen(false);
          }}
          onSubmit={handlePreviewSubmit}
          loading={previewSubmitLoading}
          previewUrlValue={contentData.previewUrl}
        />
        {isSaveLoading && <LoadingOverlay />}

      </Grid2>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Fields;
